import React, { memo } from "react";
import { IconButton } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import RefreshIcon from "@material-ui/icons/Refresh";
import RestoreIcon from "@material-ui/icons/Restore";
import ScheduleIcon from "@material-ui/icons/Schedule";
import WarningIcon from "@material-ui/icons/Warning";
import CcBadge from "./CcBadge";
import carecentreTokens from "../../tokens";
import { cx } from "../utils";
import {
  DISMISS_REASON_LABELS,
  getPathwayAccentTone,
  getPathwayEvalDotColor,
  getPathwayStatusTone,
  getPathwaySubtitle,
  getPathwaySubtitleTone,
  getPathwayToolScoreLabel,
  getPathwayToolScoreTone,
  isPathwayClickable,
  showPathwayAiBadge,
} from "./pathwayNodeDisplay";

const { color } = carecentreTokens;

const STATUS_ICON = {
  warning: WarningIcon,
  success: CheckCircleIcon,
  danger: WarningIcon,
  muted: RadioButtonUncheckedIcon,
};

function PathwayStatusIcon({ item }) {
  if (item?.action === "review") {
    return <RefreshIcon style={{ fontSize: 18, color: color.warning }} />;
  }
  const tone = getPathwayStatusTone(item);
  const Icon = STATUS_ICON[tone] || RadioButtonUncheckedIcon;
  const iconColor =
    tone === "success"
      ? color.success
      : tone === "danger"
        ? color.danger
        : tone === "warning"
          ? color.warning
          : color.lineStrong;
  return <Icon style={{ fontSize: 18, color: iconColor }} />;
}

/**
 * CcPathwayNode — care pathway task card (stacked list + flow canvas).
 */
const CcPathwayNode = memo(
  ({
    item,
    onTemplateClick,
    onDismissClick,
    onRestore,
    className,
    variant = "stacked",
    ...props
  }) => {
    const isDismissed = item?.status === "dismissed";
    const clickable = isPathwayClickable(item);
    const accentTone = getPathwayAccentTone(item);
    const subtitle = getPathwaySubtitle(item);
    const subtitleTone = getPathwaySubtitleTone(item);
    const evalDotTone = getPathwayEvalDotColor(item);
    const toolScoreTone = getPathwayToolScoreTone(item);
    const aiBadge = showPathwayAiBadge(item);
    const Root = clickable ? "button" : "div";

    return (
      <Root
        type={clickable ? "button" : undefined}
        className={cx(
          "cc-pathway-node",
          `cc-pathway-node--${variant}`,
          accentTone !== "none" && `cc-pathway-node--accent-${accentTone}`,
          clickable && "cc-pathway-node--clickable",
          isDismissed && "cc-pathway-node--dismissed",
          className,
        )}
        onClick={() => {
          if (clickable && onTemplateClick) {
            onTemplateClick(item.template_id, item.template_name, item);
          }
        }}
        onKeyDown={(e) => {
          if (clickable && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onTemplateClick?.(item.template_id, item.template_name, item);
          }
        }}
        {...props}
      >
        <span className="cc-pathway-node__status" aria-hidden>
          <PathwayStatusIcon item={item} />
          {evalDotTone ? (
            <span
              className={cx(
                "cc-pathway-node__eval-dot",
                `cc-pathway-node__eval-dot--${evalDotTone}`,
              )}
            />
          ) : null}
        </span>

        <span className="cc-pathway-node__body">
          <span
            className={cx(
              "cc-pathway-node__title",
              isDismissed && "cc-pathway-node__title--struck",
            )}
          >
            {item?.template_name}
          </span>

          {toolScoreTone ? (
            <span
              className={cx(
                "cc-pathway-node__score",
                `cc-pathway-node__score--${toolScoreTone}`,
              )}
            >
              {getPathwayToolScoreLabel(item)}
            </span>
          ) : null}

          {item?.triggered_by_name ? (
            <span className="cc-pathway-node__trigger">
              ← from {item.triggered_by_name}
            </span>
          ) : null}

          {item?.source === "ai_overlay" ? (
            <span
              className="cc-pathway-node__source-badge"
              style={{
                display: "inline-block",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: "1px 5px",
                marginTop: 2,
                borderRadius: 3,
                background: "#7A4FA8",
                color: "#fff",
                textTransform: "uppercase",
              }}
              title="Added by the AI overlay (not in your location's deterministic journey graph)"
            >
              AI
            </span>
          ) : item?.source === "rules" ? (
            <span
              className="cc-pathway-node__source-badge"
              style={{
                display: "inline-block",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: 0.5,
                padding: "1px 5px",
                marginTop: 2,
                borderRadius: 3,
                background: "#E0E0E0",
                color: "#555",
                textTransform: "uppercase",
              }}
              title="Suggested by your location's care pathway graph"
            >
              Pathway
            </span>
          ) : null}

          {subtitle ? (
            <span
              className={cx(
                "cc-pathway-node__subtitle",
                `cc-pathway-node__subtitle--${subtitleTone}`,
              )}
            >
              {subtitle}
            </span>
          ) : null}
        </span>

        <span className="cc-pathway-node__actions">
          {isDismissed ? (
            <>
              <span className="cc-pathway-node__dismiss-label">
                {DISMISS_REASON_LABELS[item?.dismiss_reason] || "Dismissed"}
              </span>
              <IconButton
                size="small"
                className="cc-pathway-node__action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore?.(e, item.template_id);
                }}
                aria-label="Restore template"
              >
                <RestoreIcon style={{ fontSize: 16 }} />
              </IconButton>
            </>
          ) : (
            <>
              {aiBadge ? (
                <CcBadge tone="ai" shape="ai">
                  AI
                </CcBadge>
              ) : null}
              {clickable && item?.status !== "completed" ? (
                <IconButton
                  size="small"
                  className="cc-pathway-node__action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismissClick?.(e, item.template_id);
                  }}
                  aria-label="Dismiss template"
                >
                  <MoreVertIcon style={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
            </>
          )}
        </span>
      </Root>
    );
  },
);

CcPathwayNode.displayName = "CcPathwayNode";

export default CcPathwayNode;
